import type { UserSummary } from '@models/api';
import type { ChatRouteState } from '@models/view';
import { routes } from '@routes';
import { isPathBuildable } from '@utils/slugHelpers';

/** What a screen knows of someone else's post when it opens a chat with the person who posted it. */
export interface PostForAuthorChat {
  id: string;
  is_anonymous: boolean;
  /** The post's owner as the post names them (`detail.user`). Never read for an anonymous post. */
  user: UserSummary | null | undefined;
}

/** Where that chat opens. */
export interface PostAuthorChat {
  /** The chat path, with its returnTo. */
  path: string;
  /** Route state for the Chat screen: the named author's id and name, or nothing naming anyone. */
  state: Pick<ChatRouteState, 'chatPeerId' | 'isAnonymousConversation' | 'profileCache'>;
  /** The author the chat names (a public post's owner), or null for a chat by the post. */
  author: UserSummary | null;
}

/**
 * The chat with the author of someone else's post, for "I can help" and the chat icon on the
 * viewer's own row (QuestionCard, QuestionDetails). The post's owner never calls this: they name
 * the other participant themselves.
 *
 * - An anonymous post goes by the post alone (`/chat/post/:questionId`): the server resolves its
 *   owner (backend docs/features/post_read_access.md §2.7). Its author fields are never read, so
 *   nothing changes when the contract PR (TECH-712) nulls them.
 * - A public post opens the pair's direct conversation with the author it names, as before: a fetch
 *   by the post returns only that post's own thread, without the rest of the conversation.
 * - A public post that names no author goes by the post too, rather than not opening at all.
 */
export function postAuthorChat(post: PostForAuthorChat, returnTo: string): PostAuthorChat {
  const author = post.is_anonymous ? null : post.user;

  if (isPathBuildable(author)) {
    return {
      path: routes.chat(author, { returnTo }),
      state: {
        chatPeerId: author.id,
        isAnonymousConversation: false,
        profileCache: {
          name: author.full_name || 'User',
          avatar: author.profile_picture || undefined,
        },
      },
      author,
    };
  }

  return {
    path: routes.chatAboutPost({ id: post.id }, { returnTo }),
    state: {
      isAnonymousConversation: post.is_anonymous,
      // The name a helper sees for an anonymous post's owner, as the server's rooms list does.
      ...(post.is_anonymous && { profileCache: { name: 'Anonymous' } }),
    },
    author: null,
  };
}
